/**
 * Neural Link Store
 * 
 * Singleton that holds the last N messages from each chat window.
 * When tiles are connected (Neural Link active), ChatInterface reads
 * peer context here and injects it into the system prompt before
 * sending to the API — so each AI literally reads what the others said.
 */

interface NeuralMessage {
  role: "user" | "assistant";
  text: string;
  aiName: string;
  timestamp: number;
  /** Which tile this came from — stamped automatically by write(), not
   *  passed in by callers. Lets buildContext() single out messages from a
   *  specific tile (e.g. the primary tile) even though aiName alone can't:
   *  two tiles can share the same AI profile and therefore the same aiName
   *  once their disambiguated display names ("Mindaloo AC 2") are stripped
   *  back out for the model. */
  sourceId: string;
}

class NeuralLinkStore {
  private messages = new Map<string, NeuralMessage[]>();
  private MAX = 6; // last 6 messages per chat window

  /** Called by ChatInterface after every message (user or assistant) */
  write(chatWindowId: string, msg: Omit<NeuralMessage, "sourceId">) {
    const existing = this.messages.get(chatWindowId) ?? [];
    const updated = [...existing, { ...msg, sourceId: chatWindowId }].slice(-this.MAX);
    this.messages.set(chatWindowId, updated);
  }

  /** Returns the last messages from all peer windows (excluding self) */
  readPeers(selfId: string, connectedIds: string[]): NeuralMessage[] {
    const peers = connectedIds.filter(id => id !== selfId);
    return peers
      .flatMap(id => this.messages.get(id) ?? [])
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-this.MAX * peers.length);
  }

  /** Build a neural link context string to prepend to the system prompt.
   *
   *  The store is one global window per tile that persists for the whole
   *  session — across an hour of testing that touches five unrelated
   *  topics, readPeers() was returning all of them jumbled together with
   *  no signal about which one is current. A vague pronoun follow-up
   *  ("what do you think about this?") had nothing to anchor "this" to,
   *  so whichever topic happened to still be in a given peer's 6-message
   *  window resolved it — or didn't — essentially at random. Confirmed
   *  from real usage: the identical broadcast landed correctly on one
   *  linked tile and came back confused from another. Marking the single
   *  most recent peer message explicitly, and telling the model to anchor
   *  bare pronoun references to it specifically, closes that gap without
   *  touching the turn-taking/backfill mechanics (already fixed) that get
   *  this context here in the first place. */
  buildContext(selfId: string, connectedIds: string[], selfAiName: string, primaryTileId?: string | null): string {
    const peers = this.readPeers(selfId, connectedIds);
    if (!peers.length) return "";

    const lastIndex = peers.length - 1;
    const hasPrimary = !!primaryTileId && primaryTileId !== selfId && peers.some((m) => m.sourceId === primaryTileId);
    const lines = peers.map((m, i) => {
      const tags = [
        i === lastIndex ? "MOST RECENT" : null,
        hasPrimary && m.sourceId === primaryTileId ? "PRIMARY DIRECTION" : null,
      ].filter(Boolean);
      const tag = tags.length ? ` — ${tags.join(", ")}` : "";
      return `[${m.aiName}/${m.role === "assistant" ? "says" : "user asked"}${tag}]: ${m.text}`;
    });

    return [
      `\n\n---`,
      `NEURAL LINK ACTIVE — You are connected to ${connectedIds.length - 1} other AI(s).`,
      `You can see what your peers have said. Read this before responding so you can build on, challenge, or collaborate with them — but stay in your own voice and identity.`,
      `If the user's message is a bare follow-up ("what do you think about this?", "are you sure?", "add more") with no explicit subject, it refers to the peer message marked MOST RECENT below, not an earlier one — resolve it against that specifically rather than guessing among older topics.`,
      hasPrimary
        ? `The user has designated one tile as the PRIMARY DIRECTION for this group — give its take real weight when you agree, and say plainly (not just quietly diverge) when you don't. Still answer honestly in your own voice; primary means "the lead thread," not "always right."`
        : null,
      ``,
      `Recent peer context (oldest to newest):`,
      ...lines,
      `---\n`,
    ].filter((l): l is string => l !== null).join("\n");
  }

  clear(chatWindowId: string) {
    this.messages.delete(chatWindowId);
  }
}

export const neuralLinkStore = new NeuralLinkStore();
